import React, { useMemo, useState } from 'react';
import { StockEntity, StockPlaceEntity } from '../types/data';
import './StockList.css';
import Icon from './icons/Icon';
import MenuButton from './MenuButton';
import IconButton from './IconButton';

interface StockListProps {
  stocks: StockEntity[];
  stockPlaces: StockPlaceEntity[];
  viewMode: 'category' | 'place';
  selectedGroupId: string | null;
  onChangeCount: (stockId: string, newCount: number) => void;
  onDeleteStock: (stockId: string) => void;
  onSelectStock?: (stockId: string) => void;
  onReorderStocks?: (sourceId: string, targetId: string, insertAfter: boolean) => void | Promise<void>;
}

const StockList: React.FC<StockListProps> = ({ stocks, stockPlaces, viewMode, selectedGroupId, onChangeCount, onDeleteStock, onSelectStock, onReorderStocks }) => {
  const [draggingStockId, setDraggingStockId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<{ id: string; insertAfter: boolean } | null>(null);
  const placeTitleMap = useMemo(() => new Map(stockPlaces.map(place => [place.id, place.title])), [stockPlaces]);

  const filtered = useMemo(() => {
    const matchesGroup = (s: StockEntity) => {
      if (viewMode === 'place') {
        if (selectedGroupId === null) return !s.stock_place_id;
        return s.stock_place_id === selectedGroupId;
      }
      if (selectedGroupId === null) return !s.stock_category_id;
      return s.stock_category_id === selectedGroupId;
    };
    return stocks
      .filter(s => !s.is_deleted && matchesGroup(s))
      .slice()
      .sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
  }, [stocks, selectedGroupId, viewMode]);

  if (filtered.length === 0) {
    return <div className="stock-list-empty text-body-large">ストックはありません</div>;
  }

  const canReorder = Boolean(onReorderStocks);

  const getClassName = (stockId: string) => {
    return [
      'stock-row',
      draggingStockId === stockId ? 'dragging' : '',
      dragOver?.id === stockId ? (dragOver.insertAfter ? 'drag-over-after' : 'drag-over-before') : '',
    ].filter(Boolean).join(' ');
  };

  const computeInsertAfter = (e: React.DragEvent<HTMLDivElement>) => {
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    return (e.clientY - rect.top) > rect.height / 2;
  };

  const handleDrop = async (targetId: string) => {
    if (!onReorderStocks) return;
    if (!draggingStockId) return;
    if (draggingStockId === targetId) return;
    const insertAfter = dragOver?.id === targetId ? dragOver.insertAfter : false;
    setDragOver(null);
    try {
      await Promise.resolve(onReorderStocks(draggingStockId, targetId, insertAfter));
    } finally {
      setDraggingStockId(null);
    }
  };

  const handleDecrement = (id: string, current: number) => {
    const next = Math.max(0, (current ?? 0) - 1);
    onChangeCount(id, next);
  };
  const handleIncrement = (id: string, current: number) => {
    const next = (current ?? 0) + 1;
    onChangeCount(id, next);
  };

  return (
    <div className="stock-list">
      {filtered.map(stock => {
        const count = stock.count ?? 0;
        const countColorClass = count <= 1 ? 'text-error' : '';
        const stockPlaceTitle = stock.stock_place_id ? (placeTitleMap.get(stock.stock_place_id) ?? '未設定') : '未設定';
        return (
          <div
            key={stock.id}
            className={getClassName(stock.id)}
            role="button"
            tabIndex={0}
            onClick={() => onSelectStock?.(stock.id)}
            draggable={canReorder}
            onDragStart={(e) => {
              if (!canReorder) return;
              e.dataTransfer.effectAllowed = 'move';
              setDraggingStockId(stock.id);
            }}
            onDragEnd={() => {
              setDraggingStockId(null);
              setDragOver(null);
            }}
            onDragOver={(e) => {
              if (!canReorder) return;
              if (!draggingStockId) return;
              if (draggingStockId === stock.id) return;
              e.preventDefault();
              setDragOver({ id: stock.id, insertAfter: computeInsertAfter(e) });
            }}
            onDragLeave={() => {
              if (dragOver?.id === stock.id) setDragOver(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              void handleDrop(stock.id);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectStock?.(stock.id);
              }
            }}
          >
          <div className="stock-row__state">
              {!stock.stock_category_id && (
                <div className="stock-row__icon" aria-hidden>
                  <Icon name="empty" size={24} />
                </div>
              )}
              <div className="stock-row__text">
                <div className="stock-row__title text-heading-medium">{stock.title}</div>
                <div className="stock-row__meta">
                  <div className="stock-row__place text-body-medium">
                    <Icon name="location" size={16} />
                    <span className="text-body-medium" title={stockPlaceTitle}>{stockPlaceTitle}</span>
                  </div>
                  <div className="stock-row__place text-body-medium">
                    <Icon name="shop" size={16} />
                    <span className="text-body-medium" title={stock.where_to_get || ''}>{stock.where_to_get || '-'}</span>
                  </div>
                  <div className="stock-row__count text-body-medium">
                    <Icon name="inventory" size={16} />
                    <span className="text-body-medium">あと</span>
                    <span className={`text-body-medium-bold ${countColorClass}`}>{count}</span>
                    <span className="text-body-medium">個</span>
                  </div>
                </div>
              </div>
              <div className="stock-row__scock-manager" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <IconButton
                  icon="minus"
                  onClick={() => handleDecrement(stock.id, count)}
                  tooltip="ストック数を減らす"
                  tooltipPosition="bottom-left"
                  aria-label="ストック数を減らす"
                />
                <IconButton
                  icon="add"
                  onClick={() => handleIncrement(stock.id, count)}
                  tooltip="ストック数を増やす"
                  tooltipPosition="bottom-left"
                  aria-label="ストック数を増やす"
                />
              </div>
              <div className="stock-row__actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <MenuButton
                  icon="menu"
                  tooltip="ストック操作メニュー"
                  tooltipPosition="bottom-left"
                  aria-label="ストック操作メニュー"
                  items={[{
                    type: 'Destructive',
                    label: 'ストックを削除',
                    leftIcon: 'delete',
                    onClick: () => onDeleteStock(stock.id)
                  }]}
                  menuWidth={180}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StockList;
